import Component, { mixins } from 'vue-class-component';
import { mapGetters, mapState } from 'vuex';

import { SurveyChoice, SurveyStepContent, ChoiceIdentifier } from 'frontend/types/nux';
import {
  SegmentationAnswer,
  SegmentationQuestion,
  SegmentationAnswerInput,
  UserSegmentation,
} from 'frontend/types/segmentation';
import { postData } from 'frontend/utils/ajax';
import { trackClickWithElementId } from 'frontend/utils/nux';
import { stringifyQueryParams } from 'frontend/utils/urls';
import { TypedDict, User } from 'frontend/types';
import { RawLocation } from 'vue-router';
import { MARKETING_LANDING_PAGES_GET_PARAM } from 'frontend/utils/marketing';
import {
  withPreservedQueryParams,
  nuxStepsData,
  returningUserNuxSteps,
} from 'frontend/views/nux/common';
import NuxExperimentsMixin from 'frontend/views/nux/mixins/NuxExperimentsMixin';

@Component({
  computed: {
    ...mapGetters('segmentationQuestion', ['segmentationQuestion']),
    ...mapGetters('userSegmentations', ['userSegmentations']),
    ...mapState('globals', ['user']),
  },
})
export default class SegmentationQuestionMixin extends mixins(NuxExperimentsMixin) {
  nextRouteQuery: TypedDict<string> = {};
  questionClass = 'nux-survey';
  loading = false;
  continueButtonText = 'Continue';
  answeredTrackId = 'clicked_nux_general_segmentation_question_continue';
  segmentationQuestion!: SegmentationQuestion;
  userSegmentations!: UserSegmentation[];
  choice: Set<ChoiceIdentifier> = new Set();
  user!: User;

  get nextRoute(): string {
    // the next route is likely to be changed dynamically as computed property
    // in the child components
    return 'nux_welcome';
  }

  get originalChoices(): SurveyChoice[] {
    // This method exists so its easy to override `get choices()` method in child components having
    // access to the original list of choices via originalChoices
    return this.segmentationQuestion.answers.map((answer: SegmentationAnswer) => {
      return {
        image_url: answer.image_url,
        title: '',
        text: answer.text,
        identifier: answer.slug,
      };
    });
  }

  get choices(): SurveyChoice[] {
    return this.originalChoices;
  }

  get currentStepContent(): SurveyStepContent {
    return {
      title: this.questionText,
      text: this.questionSubtext,
      choices: this.choices,
      stepType: 'survey',
      stepClass: this.questionClass,
    };
  }

  get questionText(): string {
    return this.segmentationQuestion.text;
  }

  get questionSubtext(): string {
    return this.segmentationQuestion.subtext || '';
  }

  async next(): Promise<void> {
    this.loading = true;
    trackClickWithElementId(this.answeredTrackId);
    await this.postUsersAnswer();
    await this.navigateTo(this.getNextStepPath());
  }

  navBack(): void {
    // this should be overridden to actually change the step in parent components
    // we fallback to the browser history to avoid runtime errors.
    this.$router.go(-1);
  }

  async postUsersAnswer(): Promise<void> {
    const answerSlugs = Array.from(this.choice).join(',');
    const query: SegmentationAnswerInput = {
      segmentation_answer_slug: answerSlugs,
      segmentation_question_slug: this.segmentationQuestion.slug,
    };
    await postData(this.$route.path, stringifyQueryParams(query));
  }

  getNextStepPath(): string {
    return this.nextRoute;
  }

  getAnswerToQuestion(slug: string): UserSegmentation | undefined {
    return this.userSegmentations?.find((segmentation: UserSegmentation) => {
      return segmentation.question_slug === slug;
    });
  }

  selectChoice(choiceIdentifier: ChoiceIdentifier): void {
    let newChoice: Set<ChoiceIdentifier>;
    if (this.segmentationQuestion?.allows_multiple) {
      newChoice = this.multipleAnswersQuestionHandler(choiceIdentifier);
    } else {
      newChoice = this.singleAnswerQuestionHandler(choiceIdentifier);
    }
    this.choice = newChoice;
  }

  singleAnswerQuestionHandler(choiceIdentifier: ChoiceIdentifier): Set<ChoiceIdentifier> {
    const newChoice = new Set<ChoiceIdentifier>();
    newChoice.add(choiceIdentifier);
    return newChoice;
  }

  multipleAnswersQuestionHandler(choiceIdentifier: ChoiceIdentifier): Set<ChoiceIdentifier> {
    const newChoice = new Set<ChoiceIdentifier>();

    // we need to make a new set for reactivity
    this.choice.forEach((choice: ChoiceIdentifier) => {
      newChoice.add(choice);
    });

    if (this.choice.has(choiceIdentifier)) {
      // choice was already selected, remove it
      newChoice.delete(choiceIdentifier);
    } else {
      // choice was not selected, add it
      newChoice.add(choiceIdentifier);
    }

    return newChoice;
  }

  isSelected(choiceIdentifier: ChoiceIdentifier): boolean {
    return this.choice.has(choiceIdentifier);
  }

  async navigateTo(routeName: string) {
    let routerObject: RawLocation = {
      name: routeName,
    };
    if (this.nextRouteQuery) {
      routerObject = { ...routerObject, query: this.nextRouteQuery };
    }
    return await this.$router.push(
      withPreservedQueryParams(this.$route, routerObject, [
        MARKETING_LANDING_PAGES_GET_PARAM,
        'tour',
        'trial',
      ]),
    );
  }

  get currentStep() {
    if (this.isReturningUser) {
      return returningUserNuxSteps[this.$route.name!];
    }
    return nuxStepsData[this.$route.name!];
  }

  get stepsCount(): number {
    if (this.isReturningUser) {
      return returningUserNuxSteps.total;
    }
    return nuxStepsData.total;
  }
}
